import React, { useState } from 'react';
import { Image } from '@fiverr-private/media';
import { AspectRatio, Container, Stack } from '@fiverr-private/layout_components';
import { Link } from '@fiverr-private/typography';
import { Overlay } from '@fiverr-private/overlay';
import { IconButton } from '@fiverr-private/button';
import type { Gql } from '@fiverr-private/dynamic_matching';
import { DownloadIcon, EyeIcon, MusicIcon, VideoIcon, FileIcon } from '@fiverr-private/visuals';
import { extractThumbnailFromAttachment, transformGqlMediaTypeToLightboxSourceType } from './utils';

const PreviewIcon: React.FC = ({ children }) => (
  <Stack alignItems="center" justifyContent="center" height="100%" borderRadius="md" borderWidth="sm" width="150px">
    {children}
  </Stack>
);

interface LightboxSourceItemProps {
  attachment: Gql.BriefAttachment;
  onClick: () => void;
}

const LightboxSourceItem: React.FC<LightboxSourceItemProps> = ({ attachment, onClick }) => {
  const [isOverlayHovered, setIsOverlayHovered] = useState(false);
  const { type, downloadUrl, id } = attachment;

  const renderPreview = () => {
    const lightboxType = transformGqlMediaTypeToLightboxSourceType(type);

    switch (lightboxType) {
      case 'audio':
        return (
          <AspectRatio ratio="1.33/1" borderRadius="md">
            <PreviewIcon>
              <MusicIcon size="md" />
            </PreviewIcon>
          </AspectRatio>
        );
      case 'video':
        return (
          <AspectRatio ratio="1.33/1" borderRadius="md">
            <PreviewIcon>
              <VideoIcon size="md" />
            </PreviewIcon>
          </AspectRatio>
        );
      case 'image':
        return (
          <AspectRatio ratio="1.33/1" borderRadius="md">
            <Image
              alt={`brief-preview-attachment-${id}`}
              src={extractThumbnailFromAttachment(attachment)}
              height="100%"
              width="150px"
              onClick={onClick}
            />
          </AspectRatio>
        );
      default:
        return (
          <PreviewIcon>
            <FileIcon size="md" />
          </PreviewIcon>
        );
    }
  };

  return (
    <Container
      borderRadius="lg"
      cursor="pointer"
      display="block"
      position="relative"
      key={id}
      onMouseEnter={() => setIsOverlayHovered(true)}
      onMouseLeave={() => setIsOverlayHovered(false)}
    >
      {isOverlayHovered && (
        <Overlay lockBodyScroll={false} position="absolute" style={{ borderRadius: '8px' }}>
          <Stack direction="row" alignItems="center" justifyContent="center" gap="4" height="100%">
            <Link href={downloadUrl || ''} target="_blank">
              <IconButton aria-label="Download" intent="primary" shape="circle">
                <DownloadIcon color="white" />
              </IconButton>
            </Link>
            <IconButton aria-label="Preview" intent="primary" shape="circle" onClick={onClick}>
              <EyeIcon color="white" />
            </IconButton>
          </Stack>
        </Overlay>
      )}

      {renderPreview()}
    </Container>
  );
};

export default LightboxSourceItem;
